import type { Participant, Transaction } from '@solara/peppol-provider';

/**
 * Deterministic seed data for mock TickStar providers — keeps the demo UI
 * populated even on first load.
 */

const NOW = Date.now();
const minutes = (n: number) => new Date(NOW - n * 60_000).toISOString();
const hours = (n: number) => new Date(NOW - n * 3600_000).toISOString();
const days = (n: number) => new Date(NOW - n * 86_400_000).toISOString();

export const SEED_PARTICIPANTS: Participant[] = [
  {
    participantId: 'iso6523-actorid-upis::0230:UAE-TRN-100000000000003',
    scheme: '0230',
    identifier: 'UAE-TRN-100000000000003',
    legalName: 'Solara Pilot Tenant A — Burj Holdings LLC',
    country: 'AE',
    status: 'PUBLISHED',
    smlActive: true,
    publishedInDirectory: true,
    endpointId: 'ep-solara-eu-prod-001',
    documentProfiles: [
      'peppol-bis-billing-3.0-invoice',
      'peppol-bis-billing-3.0-credit-note',
    ],
    createdAt: days(7),
    updatedAt: days(1),
  },
  {
    participantId: 'iso6523-actorid-upis::0230:UAE-TRN-100000000000004',
    scheme: '0230',
    identifier: 'UAE-TRN-100000000000004',
    legalName: 'Mira Logistics FZ-LLC',
    country: 'AE',
    status: 'KYC_REQUIRED',
    smlActive: false,
    publishedInDirectory: false,
    endpointId: 'ep-solara-eu-prod-001',
    documentProfiles: ['peppol-bis-billing-3.0-invoice'],
    createdAt: hours(4),
    updatedAt: hours(2),
  },
  {
    participantId: 'iso6523-actorid-upis::0230:UAE-TRN-100000000000005',
    scheme: '0230',
    identifier: 'UAE-TRN-100000000000005',
    legalName: 'Falcon Trading & Distribution',
    country: 'AE',
    status: 'ACTIVATED',
    smlActive: true,
    publishedInDirectory: false,
    endpointId: 'ep-solara-eu-prod-001',
    documentProfiles: ['peppol-bis-billing-3.0-invoice'],
    createdAt: days(2),
    updatedAt: hours(6),
  },
];

export const SEED_TRANSACTIONS: Transaction[] = [
  {
    transactionId: 'tx-mock-00000001',
    messageIdentifier: 'b3a5c1d8-91ee-4f12-9c2d-1f02ab7e8841',
    direction: 'OUTBOUND',
    state: 'ACKNOWLEDGED',
    status: 'SUCCESSFUL',
    documentType: 'urn:oasis:names:specification:ubl:schema:xsd:Invoice-2',
    senderParticipantId: 'iso6523-actorid-upis::0230:UAE-TRN-100000000000003',
    receiverParticipantId: 'iso6523-actorid-upis::0230:UAE-TRN-200000000000017',
    createdAt: minutes(33),
    completedAt: minutes(32),
    acknowledgedAt: minutes(30),
    artefacts: [
      { artefactId: 'art-tx-00000001-out', type: 'OUTBOUND_SOURCE', contentType: 'application/xml', sizeBytes: 4123 },
      { artefactId: 'art-tx-00000001-rcpt', type: 'AS4_RECEIPT', contentType: 'application/xml', sizeBytes: 1284 },
    ],
  },
  {
    transactionId: 'tx-mock-00000002',
    messageIdentifier: 'a45e9911-92aa-4c0c-8d2f-90a3122c5b07',
    direction: 'INBOUND',
    state: 'COMPLETED',
    status: 'SUCCESSFUL',
    documentType: 'urn:oasis:names:specification:ubl:schema:xsd:Invoice-2',
    senderParticipantId: 'iso6523-actorid-upis::0088:7300010000001',
    receiverParticipantId: 'iso6523-actorid-upis::0230:UAE-TRN-100000000000003',
    createdAt: minutes(14),
    completedAt: minutes(13),
    artefacts: [
      { artefactId: 'art-tx-00000002-in', type: 'INBOUND_SOURCE', contentType: 'application/xml', sizeBytes: 5780 },
    ],
  },
  {
    transactionId: 'tx-mock-00000003',
    messageIdentifier: 'c0d2244b-1042-4d6a-bb0c-4a1e8011c772',
    direction: 'OUTBOUND',
    state: 'PROCESSING',
    documentType: 'urn:oasis:names:specification:ubl:schema:xsd:Invoice-2',
    senderParticipantId: 'iso6523-actorid-upis::0230:UAE-TRN-100000000000005',
    receiverParticipantId: 'iso6523-actorid-upis::0088:7300010000003',
    createdAt: minutes(2),
    artefacts: [
      { artefactId: 'art-tx-00000003-out', type: 'OUTBOUND_SOURCE', contentType: 'application/xml', sizeBytes: 3892 },
    ],
  },
  {
    transactionId: 'tx-mock-00000004',
    messageIdentifier: 'd9117bce-7e1a-4f33-92aa-08ab28e1f200',
    direction: 'OUTBOUND',
    state: 'COMPLETED',
    status: 'FAILED',
    documentType: 'urn:oasis:names:specification:ubl:schema:xsd:Invoice-2',
    senderParticipantId: 'iso6523-actorid-upis::0230:UAE-TRN-100000000000003',
    receiverParticipantId: 'iso6523-actorid-upis::0088:7300010000099',
    createdAt: hours(3),
    completedAt: hours(3),
    errorCode: 'BR-AE-VAT-08',
    errorMessage: 'PINT-AE Schematron: tax category mismatch on line 4',
    artefacts: [
      { artefactId: 'art-tx-00000004-out', type: 'OUTBOUND_SOURCE', contentType: 'application/xml', sizeBytes: 4002 },
      { artefactId: 'art-tx-00000004-val', type: 'VALIDATION_RESULT', contentType: 'application/xml', sizeBytes: 1872 },
    ],
  },
];
